export enum CameraCaptureError {
  permissionDenied = 'permissionDenied',
  unavailable = 'unavailable',
  captureFailed = 'captureFailed',
}

interface ICaptureDimensions {
  width: number;
  height: number;
}

// Keeps captured photos well under the smallest profile-picture upload limit.
export const MAXIMUM_CAPTURE_DIMENSION = 1280;
export const CAPTURED_PHOTO_TYPE = 'image/jpeg';
const CAPTURED_PHOTO_QUALITY = 0.9;
const CAPTURED_PHOTO_NAME_PREFIX = 'camera-photo';

export const cameraConstraints: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: 'user',
    width: { ideal: MAXIMUM_CAPTURE_DIMENSION },
    height: { ideal: MAXIMUM_CAPTURE_DIMENSION },
  },
};

const permissionErrorNames = ['NotAllowedError', 'SecurityError'];

export const isCameraCaptureSupported = () =>
  typeof navigator !== 'undefined'
  && typeof navigator.mediaDevices?.getUserMedia === 'function';

export const getCameraCaptureError = (error: unknown) => {
  const isPermissionError = error instanceof DOMException
    && permissionErrorNames.includes(error.name);
  const cameraCaptureError = isPermissionError
    ? CameraCaptureError.permissionDenied
    : CameraCaptureError.unavailable;

  return cameraCaptureError;
};

export const getCaptureDimensions = (
  sourceWidth: number,
  sourceHeight: number,
): ICaptureDimensions => {
  const largestDimension = Math.max(sourceWidth, sourceHeight, 1);
  const scale = Math.min(1, MAXIMUM_CAPTURE_DIMENSION / largestDimension);

  return {
    width: Math.round(sourceWidth * scale),
    height: Math.round(sourceHeight * scale),
  };
};

export const stopMediaStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach((track) => {
    track.stop();
  });
};

export const createPhotoFileFromVideo = (
  video: HTMLVideoElement,
): Promise<File> => {
  const { width, height } = getCaptureDimensions(
    video.videoWidth,
    video.videoHeight,
  );
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');

  if (!context) {
    return Promise.reject(new Error('Canvas 2D context is unavailable'));
  }

  context.drawImage(video, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Unable to create the captured photo'));
          return;
        }

        const fileName = `${CAPTURED_PHOTO_NAME_PREFIX}-${Date.now()}.jpg`;
        resolve(new File([blob], fileName, { type: CAPTURED_PHOTO_TYPE }));
      },
      CAPTURED_PHOTO_TYPE,
      CAPTURED_PHOTO_QUALITY,
    );
  });
};
